<template>
  <SchemaTablePage
    ref="tablePageRef"
    layout-mode="tab"
    show-summary
    :search-schema="searchSchema"
    :columns="tableColumns"
    :default-query="defaultQuery"
    :request="request"
    :build-params="buildParams"
    :parse-result="parseResult"
    :table-attrs="tableAttrs"
  >
    <template #search-amountRange="{ query }">
      <div class="amount-range">
        <el-input-number v-model="query.minOrderAmount" :controls="false" placeholder="最小金额" :min="0" />
        <div style="width: 10px" />
        <el-input-number v-model="query.maxOrderAmount" :controls="false" placeholder="最大金额" :min="0" />
      </div>
    </template>

    <template #colMemberAccount="{ row }">
      <MemberAccountCell :row="row" :username="row.memberAccount" :uid="row.uid" :vip-level="row.memberLevel" />
    </template>
    <template #colOrderAmount="{ row }">{{ row.orderAmount | centsToYuan }}</template>
    <template #colOrderStatus="{ row }">
      <el-tag size="mini" :type="getStatusTagType(row.orderStatus)">{{ row.orderStatus }}</el-tag>
    </template>
  </SchemaTablePage>
</template>

<script>
import { getPayinHistory } from '@/api/finance/moneyManagement'
import { formatTimestamp, toUnixSeconds } from '@/utils/time'
import { centsToYuan, yuanToCents } from '@/utils/amount'
import { PAYIN_METHOD_OPTIONS } from './config'
import MemberAccountCell from '@/components/MemberAccountCell.vue'
import SchemaTablePage from '@/components/SchemaTablePage'

const PAYIN_STATUS_MAP = {
  1: '待确认',
  4: '已取消/失败',
  5: '存款成功',
}

export default {
  name: 'ManageDepositPendingTab',
  components: { MemberAccountCell, SchemaTablePage },
  filters: {
    centsToYuan,
  },
  data() {
    return {
      PAYIN_METHOD_OPTIONS,
      defaultQuery: {
        memberAccount: '',
        orderNo: '',
        thirdOrderNo: '',
        minOrderAmount: undefined,
        maxOrderAmount: undefined,
        payin_method: '',
        orderTimeRange: [],
      },
      searchSchema: [
        {
          prop: 'memberAccount',
          label: '会员账号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'orderNo',
          label: '订单号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'thirdOrderNo',
          label: '三方订单号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          key: 'amountRange',
          label: '订单金额',
          component: 'slot',
          slot: 'search-amountRange',
        },
        {
          prop: 'payin_method',
          label: '支付方式',
          component: 'select',
          componentProps: { clearable: true },
          options: [{ label: '全部', value: '' }, ...PAYIN_METHOD_OPTIONS],
        },
        {
          prop: 'orderTimeRange',
          label: '订单时间',
          component: 'datetime-range',
          componentProps: { clearable: true },
          // span: { xs: 24, sm: 24, md: 12, lg: 10 },
        },
        { component: 'actions' },
      ],
      tableColumns: [
        { prop: 'orderNo', label: '订单号', width: 180, resizable: false },
        { prop: 'thirdOrderNo', label: '三方订单号', minWidth: 200, resizable: false },
        { prop: 'memberAccount', label: '会员账号', width: 140, resizable: false, slot: 'colMemberAccount' },
        { prop: 'orderAmount', label: '订单金额', minWidth: 100, resizable: false, slot: 'colOrderAmount' },
        { prop: 'total_fee', label: '手续费', minWidth: 60, resizable: false },
        { prop: 'payin_method', label: '支付方式', minWidth: 100, resizable: false },
        { prop: 'orderTime', label: '订单时间', minWidth: 160, resizable: false },
        {
          fixed: 'right',
          prop: 'orderStatus',
          label: '订单状态',
          width: 100,
          align: 'center',
          resizable: false,
          slot: 'colOrderStatus',
        },
      ],
      tableAttrs: {
        showSummary: true,
        summaryMethod: (...args) => this.getSummaries(...args),
      },
    }
  },
  methods: {
    getStatusTagType(status) {
      const tagTypeMap = {
        [PAYIN_STATUS_MAP['1']]: 'primary',
        [PAYIN_STATUS_MAP['5']]: 'success',
        [PAYIN_STATUS_MAP['4']]: 'danger',
      }
      return tagTypeMap[status] || 'info'
    },
    request(params) {
      return getPayinHistory(params)
    },
    buildParams({ query, page, limit }) {
      const params = {
        page,
        page_size: limit,
        status: 1,
      }
      if (query.memberAccount) params.username = query.memberAccount
      if (query.orderNo) params.order_id = query.orderNo
      if (query.thirdOrderNo) params.third_order_id = query.thirdOrderNo
      if (query.minOrderAmount > 0) params.order_amount_start = yuanToCents(query.minOrderAmount)
      if (query.maxOrderAmount > 0) params.order_amount_end = yuanToCents(query.maxOrderAmount)
      if (query.payin_method) params.payin_method = query.payin_method
      const range = query.orderTimeRange
      if (range && range.length === 2 && range[0] && range[1]) {
        params.start_at = toUnixSeconds(range[0])
        params.end_at = toUnixSeconds(range[1])
      }
      return params
    },
    parseResult(res) {
      const list = (res && res.data && res.data.list) || []
      const total = res && res.data && res.data.total != null ? res.data.total : 0
      return {
        list: list.map(item => this.mapPayinItem(item)),
        total,
      }
    },
    mapPayinItem(item) {
      return {
        uid: item.uid,
        orderNo: item.order_id ?? item.id ?? '',
        thirdOrderNo: item.third_order_id || '',
        memberAccount: item.username || '',
        memberLevel: item.vip_level != null ? String(item.vip_level) : '',
        orderAmount: item.amount,
        total_fee: centsToYuan(item.total_fee),
        arrivalAmount: centsToYuan(item.real_amount),
        topUpAmount: centsToYuan(item.get_gold),
        depositDiscount: centsToYuan(item.discount),
        payin_method: item.payin_method || '',
        orderTime: formatTimestamp(item.created_at),
        orderStatus: PAYIN_STATUS_MAP[item.status] != null ? PAYIN_STATUS_MAP[item.status] : `状态${item.status}`,
      }
    },
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '小计（当前页）'
          return
        }
        const columnKeys = ['orderAmount']
        if (columnKeys.includes(column.property)) {
          const values = data.map(item => parseFloat(item[column.property]) || 0)
          sums[index] = values.reduce((prev, curr) => {
            const value = parseFloat(curr)
            if (!isNaN(value)) {
              return prev + curr
            }
            return prev
          }, 0)
          sums[index] = centsToYuan(sums[index])
        } else {
          sums[index] = ''
        }
      })
      return sums
    },
  },
}
</script>

<style scoped>
.amount-range {
  display: flex;
  align-items: center;
  width: 100%;
}

.amount-range .el-input-number {
  flex: 1;
  min-width: 0;
}
</style>
